import PropTypes from "prop-types"
import React, { useContext, useEffect, useRef, useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import LaunchDockwaPayModalButton from "src/main/PointOfSale/CurrentSalePanel/LaunchDockwaPayModalButton"
import { POSContext } from "src/main/PointOfSale/Layout"
import {
  GUEST_CONTACT,
  GUEST_DISPLAY_NAME,
} from "src/main/PointOfSale/constants"

import Button from "src/components/Button"
import ContactBoatPicker from "src/components/ContactBoatPicker"
import useContactBoatPicker, {
  PANEL_NONE,
} from "src/components/ContactBoatPicker/useContactBoatPicker"
import Modal from "src/components/Modal"

const EXTRA_FIELDS = ["payment_methods", "dockwa_plus"]

const EMPTY_CONTACT = { name: "", email: "", phone: "" }
const EMPTY_CONTACT_BOAT = {
  name: "",
  boat_type: "",
  length_overall: 0,
  beam: 0,
}

const PickerInner = ({ submitRef, setCustomerSelection }) => {
  const form = useFormContext()
  const picker = useContactBoatPicker({
    requireBoat: false,
    enableGuest: true,
    allowBoatFirst: true,
    emptyContact: EMPTY_CONTACT,
    emptyContactBoat: EMPTY_CONTACT_BOAT,
    extraFields: EXTRA_FIELDS,
  })

  useEffect(() => {
    submitRef.current = async () => {
      const values = form.getValues()
      const hasSelection =
        !!values.contact_id || !!values.is_guest || picker.panel !== PANEL_NONE
      if (!hasSelection) return null

      const result = await picker.resolveSelection(values)
      // Customer and boat go over in one selection: both live on the cart, and
      // two setters would mean two PATCHes racing each other.
      const selection = { boat: result.contactBoat }
      if (result.isGuest) {
        selection.contact = GUEST_CONTACT
      } else if (result.contact) {
        selection.contact = result.contact
      }
      setCustomerSelection(selection)
      return result
    }
    return () => {
      submitRef.current = null
    }
  }, [picker, form, submitRef, setCustomerSelection])

  return (
    <>
      <ContactBoatPicker
        handle={picker}
        enableGuest
        guestLabel={GUEST_DISPLAY_NAME}
        extraFields={EXTRA_FIELDS}
      />
      {form.formState.errors?.root?.picker && (
        <p className="mt-2 text-sm text-red-600">
          {form.formState.errors.root.picker.message}
        </p>
      )}
    </>
  )
}

PickerInner.propTypes = {
  submitRef: PropTypes.object.isRequired,
  setCustomerSelection: PropTypes.func.isRequired,
}

const ModalBody = ({ submitRef }) => {
  const { contact, boat, setCustomerSelection } = useContext(POSContext)

  const form = useForm({
    defaultValues: {
      contact_id: contact?.isGuest ? "" : contact?.id ?? "",
      contact: contact?.isGuest || !contact?.id ? EMPTY_CONTACT : contact,
      contact_boat_id: boat?.id ?? "",
      contact_boat: boat?.id ? boat : EMPTY_CONTACT_BOAT,
      is_guest: !!contact?.isGuest,
    },
  })

  return (
    <FormProvider {...form}>
      <PickerInner
        submitRef={submitRef}
        setCustomerSelection={setCustomerSelection}
      />
    </FormProvider>
  )
}

ModalBody.propTypes = {
  submitRef: PropTypes.object.isRequired,
}

export default function SelectCustomerModal({ isOpen, onClose }) {
  const submitRef = useRef(null)
  const [submitting, setSubmitting] = useState(false)

  const handleContinue = async () => {
    if (!submitRef.current) {
      onClose()
      return
    }
    setSubmitting(true)
    try {
      await submitRef.current()
      onClose()
    } catch (_error) {
      // Picker errors render inline below the form; keep modal open.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <Modal.Header title="Select Customer" />
      <Modal.Body controlOverflow={false}>
        {isOpen && (
          <div className="flex flex-row space-x-4">
            <div className="flex-3">
              <ModalBody submitRef={submitRef} />
            </div>
            <div className="flex-1 border-l border-gray-200 pl-4">
              <LaunchDockwaPayModalButton onClickCallback={onClose} />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end">
          <Button
            variant="primary"
            type="button"
            onClick={handleContinue}
            isLoading={submitting}
          >
            Continue
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

SelectCustomerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}
